/**
 * Notification Message Constants
 *
 * Pre-configured notification messages for consistent messaging across the LOCI platform.
 * These constants eliminate code duplication and ensure uniform user experience.
 *
 * @module notificationMessages
 */

import type { NotificationInput } from '../types/notification.types';

/**
 * Geolocation-related notification messages.
 * Used when handling location permissions and geolocation API responses.
 */
export const geolocationMessages = {
  PERMISSION_DENIED: {
    type: 'warning',
    title: 'Location Access Denied',
    message: 'Enable location services to discover events near you.',
    priority: 'normal',
    duration: 6000,
    dismissible: true,
  } as const satisfies NotificationInput,

  UNAVAILABLE: {
    type: 'error',
    title: 'Location Unavailable',
    message: 'Unable to determine your location. Please try again.',
    priority: 'normal',
    duration: 5000,
    dismissible: true,
  } as const satisfies NotificationInput,

  TIMEOUT: {
    type: 'warning',
    title: 'Location Request Timed Out',
    message: 'Location request took too long. Please try again.',
    priority: 'low',
    duration: 5000,
    dismissible: true,
  } as const satisfies NotificationInput,

  SUCCESS: {
    type: 'success',
    title: 'Location Updated',
    message: 'Showing events near your current location.',
    priority: 'low',
    duration: 3000,
    dismissible: true,
  } as const satisfies NotificationInput,
} as const;

/**
 * Network-related notification messages.
 * Used for API errors, connectivity issues, and rate limiting.
 */
export const networkMessages = {
  CONNECTION_ERROR: {
    type: 'error',
    title: 'Connection Error',
    message: 'Unable to connect to the server. Please check your internet connection.',
    priority: 'high',
    duration: 7000,
    dismissible: true,
  } as const satisfies NotificationInput,

  REQUEST_TIMEOUT: {
    type: 'warning',
    title: 'Request Timed Out',
    message: 'The server took too long to respond. Please try again.',
    priority: 'normal',
    duration: 5000,
    dismissible: true,
  } as const satisfies NotificationInput,

  SERVER_ERROR: {
    type: 'error',
    title: 'Server Error',
    message: 'Something went wrong on our end. Please try again later.',
    priority: 'high',
    duration: 6000,
    dismissible: true,
  } as const satisfies NotificationInput,

  RATE_LIMIT: {
    type: 'warning',
    title: 'Too Many Requests',
    message: 'Please slow down and try again in a moment.',
    priority: 'normal',
    duration: 5000,
    dismissible: true,
  } as const satisfies NotificationInput,
} as const;

/**
 * Search-related notification messages.
 * Used for search operations, filtering, and result feedback.
 */
export const searchMessages = {
  NO_RESULTS: {
    type: 'info',
    title: 'No Events Found',
    message: 'Try adjusting your search criteria or expanding the area.',
    priority: 'low',
    duration: 5000,
    dismissible: true,
  } as const satisfies NotificationInput,

  SEARCHING: {
    type: 'info',
    title: 'Searching...',
    message: 'Finding events in your area.',
    priority: 'low',
    duration: 3000,
    dismissible: false,
  } as const satisfies NotificationInput,

  FILTER_APPLIED: {
    type: 'success',
    title: 'Filters Applied',
    message: 'Results updated based on your filters.',
    priority: 'low',
    duration: 3000,
    dismissible: true,
  } as const satisfies NotificationInput,

  FILTER_CLEARED: {
    type: 'info',
    title: 'Filters Cleared',
    message: 'Showing all available events.',
    priority: 'low',
    duration: 3000,
    dismissible: true,
  } as const satisfies NotificationInput,
} as const;

/**
 * Event action notification messages.
 * Used for user interactions with events (save, share, etc.).
 */
export const eventMessages = {
  SAVED: {
    type: 'success',
    title: 'Event Saved',
    message: 'This event has been added to your saved events.',
    priority: 'low',
    duration: 3000,
    dismissible: true,
  } as const satisfies NotificationInput,

  REMOVED: {
    type: 'info',
    title: 'Event Removed',
    message: 'This event has been removed from your saved events.',
    priority: 'low',
    duration: 3000,
    dismissible: true,
  } as const satisfies NotificationInput,

  SHARED: {
    type: 'success',
    title: 'Event Shared',
    message: 'Event link copied to clipboard.',
    priority: 'low',
    duration: 3000,
    dismissible: true,
  } as const satisfies NotificationInput,

  LOAD_ERROR: {
    type: 'error',
    title: 'Failed to Load Event',
    message: 'Unable to load event details. Please try again.',
    priority: 'normal',
    duration: 5000,
    dismissible: true,
  } as const satisfies NotificationInput,
} as const;

/**
 * Authentication-related notification messages.
 * Used for login, logout, and session management.
 */
export const authMessages = {
  LOGIN_REQUIRED: {
    type: 'warning',
    title: 'Login Required',
    message: 'Please sign in to access this feature.',
    priority: 'normal',
    duration: 5000,
    dismissible: true,
  } as const satisfies NotificationInput,

  LOGIN_SUCCESS: {
    type: 'success',
    title: 'Welcome Back!',
    message: 'You have successfully signed in.',
    priority: 'normal',
    duration: 4000,
    dismissible: true,
  } as const satisfies NotificationInput,

  LOGOUT_SUCCESS: {
    type: 'info',
    title: 'Signed Out',
    message: 'You have been successfully signed out.',
    priority: 'low',
    duration: 4000,
    dismissible: true,
  } as const satisfies NotificationInput,

  SESSION_EXPIRED: {
    type: 'warning',
    title: 'Session Expired',
    message: 'Your session has expired. Please sign in again.',
    priority: 'high',
    duration: 6000,
    dismissible: true,
  } as const satisfies NotificationInput,
} as const;

/**
 * OAuth-related notification messages.
 * Used for OAuth provider linking conflicts and errors that need
 * actionable CTAs surfaced to the user.
 */
export const oauthMessages = {
  /**
   * Surfaced when a user attempts to link a provider identity that is already
   * associated with a *different* LOCI account (`409
   * PROVIDER_LINKED_TO_OTHER_ACCOUNT` from `/api/v1/auth/oauth/:provider/link`).
   *
   * Copy is intentionally provider-agnostic ("That account…") so it stays
   * accurate as Apple, Passkey, and any future identity providers come
   * online under the same code path.
   *
   * Duration is at least 6s (FRE-701 acceptance criterion) so the user has
   * time to read the recovery options before the toast auto-dismisses.
   * The `support@loci.io` address is intentionally embedded in the
   * message body — the toast renderer surfaces a single `action` button
   * for the primary CTA, so the support fallback travels in the copy.
   */
  PROVIDER_LINKED_TO_OTHER_ACCOUNT: {
    type: 'error',
    title: 'Account Already Linked',
    message:
      'That account is already linked to a different LOCI account. Sign in with that account instead, or contact support at support@loci.io.',
    priority: 'high',
    duration: 6000,
    dismissible: true,
  } as const satisfies NotificationInput,
} as const;

/**
 * Apple Music connect edge-state notification messages (WEB-446).
 * Copy mirrors `OAUTH_ERROR_STRINGS` in `apps/web/lib/oauth/errors.ts` (the
 * inline card copy) — these toast variants are shorter, for surfaces where
 * the connect card itself isn't visible (e.g. a background reconnect banner).
 */
export const appleMusicMessages = {
  CONNECT_SUCCESS: {
    type: 'success',
    title: 'Apple Music connected',
    message: 'Your library is now shaping your recommendations.',
    priority: 'normal',
    duration: 4000,
    dismissible: true,
  } as const satisfies NotificationInput,

  CONNECT_FAILED: {
    type: 'error',
    title: 'Couldn’t finish connecting',
    message: 'Apple approved it, but we couldn’t save the connection. Please try again.',
    priority: 'normal',
    duration: 6000,
    dismissible: true,
  } as const satisfies NotificationInput,

  /** One-shot toast fired at the moment an expired token is first detected. */
  TOKEN_EXPIRED: {
    type: 'warning',
    title: 'Apple Music session expired',
    message: 'Reconnect to keep your matches tuned.',
    priority: 'normal',
    duration: 6000,
    dismissible: true,
  } as const satisfies NotificationInput,

  NOT_SUBSCRIBED: {
    type: 'info',
    title: 'No active Apple Music subscription',
    message: 'There’s nothing for LOCI to read yet — everything else still works.',
    priority: 'low',
    duration: 5000,
    dismissible: true,
  } as const satisfies NotificationInput,

  /**
   * Repeatable nudge for a surface where the expiry was already known (e.g.
   * re-shown after the user dismisses the `AppleMusicReconnectPrompt` banner
   * and later takes an action that needs a live connection) — distinct from
   * the one-shot `TOKEN_EXPIRED` toast fired at first detection.
   */
  RECONNECT_REQUIRED: {
    type: 'warning',
    title: 'Reconnect Apple Music',
    message: 'Your session expired — reconnect to keep your matches tuned.',
    priority: 'normal',
    duration: 6000,
    dismissible: true,
  } as const satisfies NotificationInput,
} as const;

/**
 * System-level notification messages.
 * Used for general system feedback and status updates.
 */
export const systemMessages = {
  SUCCESS: {
    type: 'success',
    title: 'Success',
    message: 'Operation completed successfully.',
    priority: 'normal',
    duration: 4000,
    dismissible: true,
  } as const satisfies NotificationInput,

  ERROR: {
    type: 'error',
    title: 'Error',
    message: 'An unexpected error occurred. Please try again.',
    priority: 'high',
    duration: 5000,
    dismissible: true,
  } as const satisfies NotificationInput,

  COMING_SOON: {
    type: 'info',
    title: 'Coming Soon',
    message: 'This feature is under development.',
    priority: 'low',
    duration: 4000,
    dismissible: true,
  } as const satisfies NotificationInput,

  MAINTENANCE: {
    type: 'warning',
    title: 'Scheduled Maintenance',
    message: 'The system will be temporarily unavailable for maintenance.',
    priority: 'high',
    duration: 10000,
    dismissible: true,
  } as const satisfies NotificationInput,
} as const;

/**
 * Combined notification messages object for convenient access.
 * Provides namespaced access to all message categories.
 *
 * @example
 * ```typescript
 * import { notificationMessages } from '@loci/store';
 *
 * // Access a specific message
 * showNotification(notificationMessages.geolocation.PERMISSION_DENIED);
 * ```
 */
export const notificationMessages = {
  geolocation: geolocationMessages,
  network: networkMessages,
  search: searchMessages,
  event: eventMessages,
  auth: authMessages,
  oauth: oauthMessages,
  appleMusic: appleMusicMessages,
  system: systemMessages,
} as const;

/**
 * Helper function to create a customized notification from a base message.
 * Allows overriding specific properties while maintaining type safety.
 *
 * @param baseMessage - The base notification message to customize
 * @param overrides - Properties to override in the base message
 * @returns A new notification input with the overrides applied
 *
 * @example
 * ```typescript
 * import { createNotification, geolocationMessages } from '@loci/store';
 *
 * // Create a customized notification
 * const customNotification = createNotification(
 *   geolocationMessages.PERMISSION_DENIED,
 *   { message: 'Please enable location in your browser settings.' }
 * );
 * ```
 */
export function createNotification<T extends NotificationInput>(
  baseMessage: T,
  overrides: Partial<NotificationInput> = {},
): NotificationInput {
  return {
    ...baseMessage,
    ...overrides,
  };
}
